import React, { useCallback, useState, useEffect } from 'react'
import ReactTooltip from 'react-tooltip'
import { Column } from 'react-table'
import { Link, useHistory } from 'react-router-dom'

import API from '../../../../services/api'
import Icon from '../../../atoms/icon/Icon'
import Clickable from '../../../atoms/clickable/Clickable'
import DeleteParticipant from './DeleteParticipant'
import Text from '../../../atoms/text/Text'
import InlineGroup from '../../../atoms/inlinegroup/InlineGroup'
import ParticipantImport from './ParticipantImport'

import StickyBar from '../../../organisms/sticky-bar/StickyBar'
import SortableTable from '../../../organisms/sortable-table/Sortable-table'
import IconButton from '../../../molecules/iconbutton/IconButton'


interface Props {
  participants: API.ParticipantResponse[]
  eventId: string
  refreshEvent: () => void
  data: any
}

interface ParticipantState {
  importModalOpen: boolean
  deleteParticipantModalOpen: boolean
  participantToDelete: API.ParticipantResponse | null
}

const STICKY_BAR_HEIGHT = 12

function ParticipantsSection({ participants, eventId, refreshEvent, data }: Props) {
  const [{
    importModalOpen,
    deleteParticipantModalOpen,
    participantToDelete
  }, setParticipantModalState] = useState<ParticipantState>({
    importModalOpen: false,
    deleteParticipantModalOpen: false,
    participantToDelete: null
  })
  const history = useHistory()

  const [selected, setSelected] = useState<API.ParticipantResponse[]>([])

  const selectedRowsCallback = useCallback((selectedRows: API.ParticipantResponse[]) => {
    setSelected(selectedRows)
  }, [])

  const handleExport = () => {
    if (data) API.exportParticipants(data)
  }

  const onParticipantDeleteComplete = useCallback(() => {
    setParticipantModalState(prevState => ({
      ...prevState,
      deleteParticipantModalOpen: false,
      participantToDelete: null
    }))
    refreshEvent()
  }, [refreshEvent])

  const onParticipantImportComplete = useCallback(() => {
    setParticipantModalState(prevState => ({ ...prevState, importModalOpen: false }))
    refreshEvent()
  }, [refreshEvent])

  useEffect(() => {
    ReactTooltip.rebuild()
  })

  const tableColumns: Column<API.ParticipantResponse>[] = React.useMemo(() => [
    {
      Header: 'Team #',
      accessor: 'team'
    },
    {
      Header: 'CEO',
      accessor: (row) => row.isCeo ? 'Yes' : 'No'
    },
    {
      Header: 'First Name',
      accessor: 'firstName'
    },
    {
      Header: 'Last Name',
      accessor: 'lastName'
    },
    {
      Header: 'Email',
      accessor: 'email'
    },
    {
      Header: 'Actions',
      id: 'actions',
      Cell: ({ row }: { row: { original: API.ParticipantResponse } }) => (
        <>
          <Link data-test={`view-${row.original.id}`} to={`/events/${eventId}/participants/${row.original.id}/view`}>
            <Icon type='search' tip={{ content: 'View participant' }} />
          </Link>
          <Clickable
            data-test={`delete-${row.original.id}`}
            onClick={(event) => {
              event.stopPropagation()
              setParticipantModalState(prevState => ({
                ...prevState,
                deleteParticipantModalOpen: true,
                participantToDelete: row.original
              }))
            }}>
            <Icon type='trash' tip={{ content: 'Delete participant' }} />
          </Clickable>
        </>
      ),
    },
  ], [eventId])

  const onRowClick = (event: { stopPropagation: () => void, preventDefault: () => void }, row: { original: { id: string } }) => {
    event.stopPropagation()
    event.preventDefault()
    history.push(`/events/${eventId}/participants/${row.original.id}/view`)
  }

  return (
    <span className='participant-section'>
      <DeleteParticipant
        isOpen={deleteParticipantModalOpen}
        participants={participantToDelete ? [participantToDelete] : selected}
        eventId={eventId}
        onClose={() => setParticipantModalState(prevState => ({
          ...prevState,
          deleteParticipantModalOpen: false,
          participantToDelete: null
        }))}
        onComplete={onParticipantDeleteComplete}
      />
      {importModalOpen && <ParticipantImport
        isOpen={importModalOpen}
        onClose={() => setParticipantModalState(prevState => ({ ...prevState, importModalOpen: false }))}
        onComplete={onParticipantImportComplete}
        eventId={eventId}
      />}
      <StickyBar spread verticalCenter height={STICKY_BAR_HEIGHT}>
        <h2>Event Participants</h2>
        <InlineGroup spaceLeft={2}>
          <Link data-test='add-participant' to={`/events/${eventId}/participants/add`}>
            <IconButton
              icon='addUser'
              text='Add Participant'
            />
          </Link>
          <IconButton
            data-test='import-participant'
            icon='import'
            text='Import Participants'
            onClick={() => setParticipantModalState(prevState => ({ ...prevState, importModalOpen: true }))}
          />
          <IconButton
            data-test='export-participant'
            icon='export'
            text='Export Participants'
            onClick={handleExport}
          />
          <IconButton
            danger
            data-test='delete-participants'
            icon='trash'
            text='Delete Selected'
            onClick={(event) => {
              event.stopPropagation()
              setParticipantModalState(prevState => ({ ...prevState, deleteParticipantModalOpen: true }))
            }}
          />
        </InlineGroup>
      </StickyBar>
      {!data.participants.length && <Text colour='secondaryDark3' size='lg'>There are no participants yet</Text>}
      {!!data.participants.length && (
        <SortableTable stickyHeader stickyTop={STICKY_BAR_HEIGHT} clickable columns={tableColumns} data={participants} selectedRowsCallback={selectedRowsCallback} onRowClick={onRowClick} />
      )}
    </span>
  )
}

export default ParticipantsSection
